import React, {ChangeEvent, ReactNode, useRef, useState} from 'react';
import moment, {Moment, MomentInput} from 'moment';
import {Box, Button, FormHelperText, IconButton, Link, MenuItem, Popover, TextField, Tooltip} from '@mui/material';
import {Event, EventBusy} from '@mui/icons-material';
import DatePicker from '../../inputs/DatePicker/DatePicker';
import {SortFilterItemContainer} from './SortFilterItemContainer';
import {DateFormats} from '../../../constants/securspace-constants';
import {
  DATE_RANGE_PRESETS,
  DateRange,
  DateRangeChange,
  DateRangePresetKey,
  findDateRangePreset,
  getDateRangeForPreset,
} from '../../../../util/ReportDateRangeUtils';

export interface SortFilterDateRangeProps {
  startDate?: string | null;
  endDate?: string | null;
  /** Receives only what changed, in one call; a cleared date is ''. */
  onChange: (change: DateRangeChange) => void;
  /** Set false for reports that only filter on a start date. */
  showEndDate?: boolean;
  /** Set true to show start/end pickers inline instead of the "Custom range" option and the ID-search toggle. */
  showDatePickers?: boolean;
  startLabel?: string;
  endLabel?: string;
  disableFuture?: boolean;
  minDate?: MomentInput;
  maxDate?: MomentInput;
  /** True when an ID filter (invoice number, booking number...) has a value. */
  idSearchActive?: boolean;
  /** Names the ID filters in the notice, e.g. "invoice or booking number". */
  idSearchLabel?: string;
  /** True when the user chose to keep the dates while searching by ID. */
  applyDates?: boolean;
  /** Grid items rendered after the pickers but before the ID-search notice, so they share the row. */
  trailingItems?: ReactNode;
}

const toDay = (date: Moment | null) => (date && date.isValid() ? date.format(DateFormats.DAY) : '');

const normalizeDay = (value?: string | null) =>
  toDay(value ? moment(value, [DateFormats.DAY, moment.ISO_8601]) : null);

const displayDay = (value?: string | null) => normalizeDay(value) || 'Any';

const toggleSx = (on: boolean) => ({
  width: '1.7em',
  height: '1.7em',
  mt: '0.5em',
  ml: '0.5em',
  p: 0,
  border: 2,
  borderColor: 'primary.main',
  bgcolor: on ? 'primary.main' : 'transparent',
  color: on ? 'primary.contrastText' : 'primary.main',
  '&:hover': {bgcolor: on ? 'primary.dark' : 'action.hover'},
});

/**
 * Date range preset select plus start/end pickers, rendered as {@link SortFilterItemContainer}s so it drops into a
 * sort/filter row. While an ID search is active it shows All Dates over the kept range; choosing any dates applies them.
 */
const SortFilterDateRange = ({
  startDate,
  endDate,
  onChange,
  showEndDate = true,
  showDatePickers = false,
  startLabel = 'Start Date',
  endLabel = 'End Date',
  disableFuture,
  minDate,
  maxDate,
  idSearchActive = false,
  idSearchLabel = 'number',
  applyDates = false,
  trailingItems,
}: SortFilterDateRangeProps) => {
  const datesIgnored = idSearchActive && !applyDates;
  const storedPreset = findDateRangePreset(startDate, endDate, {startOnly: !showEndDate});
  const preset = datesIgnored ? 'ALL_DATES' : storedPreset;
  const rangeText = [displayDay(startDate), showEndDate && displayDay(endDate)].filter(Boolean).join(' \u2013 ');
  const customLabel = showDatePickers ? 'Custom' : rangeText;
  const storedRangeLabel = storedPreset === 'CUSTOM'
    ? rangeText
    : DATE_RANGE_PRESETS.find(option => option.key === storedPreset)?.label;
  const toggleTitle = datesIgnored
    ? `Searching all dates by ${idSearchLabel}. Click to apply the date range (${storedRangeLabel}).`
    : `Filtering by ${storedRangeLabel}. Click to search all dates by ${idSearchLabel}.`;
  const showToggle = idSearchActive && !showDatePickers;
  // During an ID search a chosen date turns the range back on; the stored range is kept for when the ID is cleared.
  const withApply = (change: DateRangeChange): DateRangeChange => (idSearchActive ? {...change, applyDates: true} : change);

  const anchorRef = useRef<HTMLDivElement>(null);
  const [customOpen, setCustomOpen] = useState(false);
  const [draft, setDraft] = useState<DateRange>({startDate: '', endDate: ''});
  // The picker drops invalid or out-of-bounds entries without calling onChange, so track its errors separately.
  const [draftErrors, setDraftErrors] = useState({startDate: false, endDate: false});
  const draftHasError = draftErrors.startDate || (showEndDate && draftErrors.endDate);
  const draftInvalid = !!draft.startDate && !!draft.endDate && showEndDate
    && moment(draft.endDate, DateFormats.DAY).isBefore(moment(draft.startDate, DateFormats.DAY));

  const openCustom = () => {
    setDraft({startDate: normalizeDay(startDate), endDate: normalizeDay(endDate)});
    setDraftErrors({startDate: false, endDate: false});
    setCustomOpen(true);
  };

  const applyCustom = () => {
    onChange(withApply(showEndDate ? draft : {startDate: draft.startDate}));
    setCustomOpen(false);
  };

  const handlePresetChange = (event: ChangeEvent<HTMLInputElement>) => {
    const key = event.target.value as DateRangePresetKey;
    if (key === 'CUSTOM') {
      return;
    }
    if (idSearchActive && key === 'ALL_DATES') {
      onChange({applyDates: false});
      return;
    }
    const range = getDateRangeForPreset(key);
    if (range) {
      onChange(withApply(showEndDate ? range : {startDate: range.startDate}));
    }
  };

  return <>
    <SortFilterItemContainer>
      <Box sx={{display: 'flex', alignItems: 'center'}}>
        <TextField
          ref={anchorRef}
          select
          fullWidth
          variant="standard"
          label="Date Range"
          value={preset}
          onChange={handlePresetChange}
          SelectProps={{
            renderValue: (value) => value === 'CUSTOM'
              ? customLabel
              : DATE_RANGE_PRESETS.find(option => option.key === value)?.label,
          }}
        >
          {DATE_RANGE_PRESETS.map((option, index) => <MenuItem
            key={option.key}
            value={option.key}
            divider={!showDatePickers && index === DATE_RANGE_PRESETS.length - 1}
          >
            {option.label}
          </MenuItem>)}
          {showDatePickers
            ? preset === 'CUSTOM' && <MenuItem value="CUSTOM" disabled>Custom</MenuItem>
            : <MenuItem value="CUSTOM" onClick={openCustom}>{'Custom range\u2026'}</MenuItem>}
        </TextField>
        {showToggle && <Tooltip title={toggleTitle} arrow slotProps={{tooltip: {sx: {typography: 'body2'}}}}>
          <IconButton
            aria-label={toggleTitle}
            aria-pressed={!datesIgnored}
            onClick={() => onChange({applyDates: datesIgnored})}
            sx={toggleSx(!datesIgnored)}
          >
            {datesIgnored ? <EventBusy fontSize="small"/> : <Event fontSize="small"/>}
          </IconButton>
        </Tooltip>}
      </Box>
      {!showDatePickers && <Popover
        open={customOpen}
        anchorEl={anchorRef.current}
        onClose={() => setCustomOpen(false)}
        anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
      >
        <Box sx={{p: 2, width: 300, display: 'flex', flexDirection: 'column', gap: 2}}>
          <DatePicker
            label={startLabel}
            value={draft.startDate}
            onChange={(date) => setDraft(prev => ({...prev, startDate: toDay(date)}))}
            onError={(error: unknown) => setDraftErrors(prev => ({...prev, startDate: !!error}))}
            disableFuture={disableFuture}
            minDate={minDate}
            maxDate={maxDate}
          />
          {showEndDate && <DatePicker
            label={endLabel}
            value={draft.endDate}
            onChange={(date) => setDraft(prev => ({...prev, endDate: toDay(date)}))}
            onError={(error: unknown) => setDraftErrors(prev => ({...prev, endDate: !!error}))}
            disableFuture={disableFuture}
            minDate={minDate}
            maxDate={maxDate}
          />}
          {draftHasError && <FormHelperText error>Enter a valid date in range.</FormHelperText>}
          {draftInvalid && <FormHelperText error>{endLabel} is before {startLabel.toLowerCase()}.</FormHelperText>}
          <Box sx={{display: 'flex', justifyContent: 'flex-end', gap: 1}}>
            <Button onClick={() => setCustomOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={applyCustom} disabled={draftInvalid || draftHasError}>Apply</Button>
          </Box>
        </Box>
      </Popover>}
    </SortFilterItemContainer>
    {showDatePickers && <SortFilterItemContainer>
      <DatePicker
        label={startLabel}
        value={datesIgnored ? null : startDate}
        onChange={(date) => onChange(withApply({startDate: toDay(date)}))}
        disableFuture={disableFuture}
        minDate={minDate}
        maxDate={maxDate}
      />
    </SortFilterItemContainer>}
    {showDatePickers && showEndDate && <SortFilterItemContainer>
      <DatePicker
        label={endLabel}
        value={datesIgnored ? null : endDate}
        onChange={(date) => onChange(withApply({endDate: toDay(date)}))}
        disableFuture={disableFuture}
        minDate={minDate}
        maxDate={maxDate}
      />
    </SortFilterItemContainer>}
    {trailingItems}
    {idSearchActive && showDatePickers && <SortFilterItemContainer xs={12} sm={12}>
      <FormHelperText>
        {datesIgnored ? `Searching all dates by ${idSearchLabel}. ` : ''}
        <Link
          component="button"
          type="button"
          variant="caption"
          onClick={() => onChange({applyDates: datesIgnored})}
        >
          {datesIgnored ? 'Use the date range' : `Search all dates by ${idSearchLabel}`}
        </Link>
      </FormHelperText>
    </SortFilterItemContainer>}
  </>;
};

export {SortFilterDateRange};
